import { fabric } from 'fabric';
import type { Bezier } from 'bezier-js';
import defaults from 'lodash-es/defaults';
import type { FabricTexture } from '../fabric-texture.class';
import type { Texture } from '../texture.class';
import BaseMode, { SUB_THEME_COLOR, THEME_COLOR } from './base.class';
import { calcFabricCanvasCoord, calcFabricRelativeCoord, calcIntersection } from '@utils';

/**
 * 贴图变形区域的顶点类型枚举
 *
 * 用于标识变形区域四个角的位置。
 *
 * @enum {string}
 */
export enum VertexType {
  /** 左上角顶点 */
  TOP_LEFT = 'tl',
  /** 右上角顶点 */
  TOP_RIGHT = 'tr',
  /** 左下角顶点 */
  BOTTOM_LEFT = 'bl',
  /** 右下角顶点 */
  BOTTOM_RIGHT = 'br',
}

/**
 * 倾斜模式配置
 */
interface SkewOptions {
  /**
   * 是否启用空白区域拖拽调整功能
   *
   * 启用后：
   * - 可以在画布空白处拖拽来调整变形区域
   * - 最小拖拽区域为 50x50 像素
   *
   * @default true
   */
  enableDragResize?: boolean;
}

/**
 * 倾斜变形模式类
 *
 * 提供图像的倾斜变形功能，通过四个边的拖拽控制点实现自由倾斜效果。
 *
 * 主要特性：
 * - 四边独立控制：每条边都有独立的拖拽控制点
 * - 智能吸附：拖拽时自动吸附到合适的倾斜角度
 * - 实时预览：拖拽过程中实时显示变形效果
 * - 防止变形过度：内置边界检查机制
 *
 * 交互方式：
 * - 拖拽边缘控制点：调整单边倾斜角度
 * - 空白处拖拽：快速调整整体大小
 * - 支持撤销/重做
 *
 * @example
 * ```typescript
 * // 1. 基础用法
 * const canvas = new fabric.Canvas('canvas');
 * const fabricTexture = new FabricTexture(canvas);
 *
 * // 创建倾斜模式并应用到图像
 * const skew = new Skew('#FF0000');  // 使用红色主题
 * fabricTexture.enterEditing(image, null, skew);
 *
 * // 2. 自定义拖拽图标样式
 * const skew = new Skew()
 *   .setStyleSetters({
 *     dragIcon: (icon) => {
 *       icon.set({ opacity: 0.8 });
 *       return icon;
 *     }
 *   });
 *
 * // 3. 隐藏原始对象（可选）
 * image.set({ visible: false });
 * canvas.renderAll();
 * ```
 *
 * @remarks 原始对象默认保持可见，需要手动隐藏
 *
 * @see
 * - {@link BaseMode} 变形模式基类
 * - {@link FabricTexture} 主要功能类
 */
class Skew extends BaseMode<{
  /**
   * 可配置拖动图标样式的方法
   * @param object - 默认的拖动图标对象
   * @returns 作为拖动图标对象的fabric元素对象，可使用默认对象以外的新对象
   */
  dragIcon: (object: fabric.Group) => fabric.Object;
}> {
  /**
   * 变形模式的唯一标识名称
   */
  public name = 'Skew';

  /**
   * 模式配置
   */
  public options: Required<SkewOptions> = {
    enableDragResize: true,
  };

  /**
   * 样式设置器集合
   *
   * 用于自定义倾斜变形模式中各种元素的样式：
   * - texture: 配置变形后的贴图样式
   * - path: 配置网格边界线的样式
   * - dragIcon: 配置拖拽控制点的样式
   *
   * @example
   * ```typescript
   * skew.setStyleSetters({
   *   // 自定义拖拽图标样式
   *   dragIcon: (icon) => {
   *     icon.set({
   *       opacity: 0.8,
   *       scaleX: 1.2,
   *       scaleY: 1.2
   *     });
   *     return icon;
   *   }
   * });
   * ```
   *
   * @protected 仅供子类访问和修改
   */
  protected _styleSetters = {
    texture: () => {},
    path: () => {},
    dragIcon: (object: fabric.Group) => object,
  };

  /**
   * 控制点映射表
   *
   * 存储控制点与其相关数据的映射关系，包括：
   * - curve: 控制点所在的贝塞尔曲线
   * - rowIndex: 控制点所在的网格行索引
   * - colIndex: 控制点所在的网格列索引
   * - vertexTypes: 控制点影响的顶点类型数组
   *
   * @internal 仅供内部使用
   */
  private _objectControlMap = new Map<
    fabric.Object,
    {
      curve: Bezier;
      rowIndex: number;
      colIndex: number;
      vertexTypes: VertexType[];
    }
  >();

  /**
   * 构造函数
   *
   * 创建一个倾斜变形模式实例。
   *
   * @param themeColor - 主题色，用于控制点的背景色，默认为 THEME_COLOR
   * @param subThemeColor - 副主题色，用于辅助元素的颜色，默认为 SUB_THEME_COLOR
   */
  constructor(
    themeColor = THEME_COLOR,
    subThemeColor = SUB_THEME_COLOR,
    options: SkewOptions = {},
  ) {
    super(themeColor, subThemeColor);

    this.options = defaults(options, this.options);
  }

  /**
   * 创建拖拽控制图标
   *
   * 创建一个双向箭头的拖拽控制图标，用于调整倾斜角度。图标包含：
   * - 圆角矩形背景
   * - 双向箭头图标
   * - 自适应旋转
   *
   * 样式特点：
   * - 背景：46x18 像素，圆角 10px
   * - 箭头：白色填充
   * - 边框：2px 白色描边
   * - 缩放：整体缩放到 0.8 倍
   * - 定位：居中对齐
   *
   * @param themeColor - 控制图标的背景颜色
   * @returns {fabric.Group} 返回组合的 Fabric 对象
   *
   * @example
   * ```typescript
   * // 创建一个红色背景的控制图标
   * const icon = Skew.createDragIcon('#FF0000');
   * canvas.add(icon);
   * ```
   */
  static createDragIcon(themeColor: string): fabric.Group {
    const arrow = new fabric.Path(
      'M 0 0.75 h 8.5 v 3.5 L 16 0 L 8.5 -4.25 v 3.5 h -17 v -3.5 L -16 0 L -8.5 4.25 v -3.5 Z',
      {
        left: 0,
        top: 0,
        scaleX: 0.8,
        scaleY: 0.8,
        fill: '#ffffff',
        originX: 'center',
        originY: 'center',
      },
    );

    const background = new fabric.Rect({
      left: 0,
      top: 0,
      width: 46,
      height: 18,
      rx: 10,
      ry: 10,
      fill: themeColor,
      stroke: '#ffffff',
      strokeWidth: 2,
      scaleX: 0.8,
      scaleY: 0.8,
      originX: 'center',
      originY: 'center',
    });

    const button = new fabric.Group([background, arrow], {
      originX: 'center',
      originY: 'center',
      hasControls: false,
      hasBorders: false,
    });
    return button;
  }

  /**
   * 执行倾斜变形的网格分割算法
   *
   * 根据贴图的网格线计算所有交点坐标，生成变形后的网格点阵。
   *
   * 算法步骤：
   * 1. 遍历网格的行和列
   * 2. 计算每个网格单元的垂直和水平线段
   * 3. 计算所有线段的交点坐标
   * 4. 生成最终的分割点数组
   *
   * @param texture - 需要进行倾斜变形的贴图对象
   * @returns {Coord[][][]} 返回三维数组，表示网格的分割点坐标：
   * - 第一维：行索引
   * - 第二维：列索引
   * - 第三维：点的坐标 {x, y}
   *
   * @example
   * ```typescript
   * const points = Skew.execute(texture);
   * // points[0][0][0] 表示左上角第一个分割点的坐标
   * ```
   */
  static execute(texture: Texture): Coord[][][] {
    const splitPoints: Coord[][][] = [];

    texture.areaBoundCurves.forEach((row, rowIndex) => {
      const _row: Coord[][] = [];
      row.forEach((col, colIndex) => {
        const _col: Coord[] = [];
        const { vertical, horizontal } = texture.mesh[rowIndex][colIndex];
        for (let h = 0; h < horizontal.length; h++) {
          for (let v = 0; v < vertical.length; v++) {
            const line0 = vertical[v];
            const line1 = horizontal[h];
            _col.push(
              calcIntersection(line0.points[0], line0.points[3], line1.points[0], line1.points[3])!,
            );
          }
        }
        _row.push(_col);
      });
      splitPoints.push(_row);
    });

    return splitPoints;
  }

  /**
   * 获取所有控制点对象
   *
   * 返回当前倾斜变形模式中的所有控制点对象。用于：
   * - 批量修改控制点样式
   * - 控制点的显示/隐藏
   * - 获取控制点位置信息
   *
   * @returns {fabric.Object[]} 所有控制点对象的数组
   */
  get controlObjects(): fabric.Object[] {
    return Array.from(this._objectControlMap.keys());
  }

  /**
   * 执行倾斜变形计算
   *
   * 代理方法，调用静态方法 {@link Skew.execute} 进行实际的倾斜变形计算。
   *
   * @param texture - 需要进行倾斜变形的贴图对象
   * @returns 返回计算后的网格分割点坐标数组
   * @see {@link Skew.execute} 具体实现细节
   */
  execute(texture: Texture) {
    return Skew.execute(texture);
  }

  /**
   * 渲染倾斜变形的交互界面
   *
   * 在画布上创建并管理倾斜变形的交互控制界面。主要功能：
   *
   * 1. 控制点管理
   * - 为每条边创建双向箭头控制点
   * - 自动计算控制点位置和角度
   * - 保持控制点始终在最上层
   *
   * 2. 交互功能
   * - 拖拽边缘控制点：调整单边倾斜角度
   * - 空白处拖拽：快速调整整体大小
   * - 智能吸附：自动对齐到水平或垂直位置
   *
   * 3. 数据同步
   * - 实时更新贴图顶点坐标
   * - 自动记录可撤销的操作历史
   * - 防止无效的变形操作
   *
   * @param fabricTexture - FabricTexture 实例
   * @returns {Function} 返回清理函数，用于移除所有控制点和事件监听器
   *
   * @example
   * ```typescript
   * // 进入倾斜编辑模式
   * const cleanup = skew.dirtyRender(fabricTexture);
   *
   * // 退出编辑模式时清理
   * cleanup();
   * ```
   *
   * @remarks
   * 1. 控制点样式可通过 setStyleSetters 自定义
   * 2. 拖拽区域小于 50x50 像素时不会触发变形
   * 3. 变形操作会自动记录到历史栈中
   */
  dirtyRender(fabricTexture: FabricTexture) {
    const fabricCanvas = fabricTexture.canvas;
    if (!fabricCanvas) return;

    const areaBounds = fabricTexture.texture?.areaBoundCurves;
    if (!areaBounds) return;

    // 更新位置和角度
    const updatePositionAndAngle = (rowindex: number, colIndex: number, direction: string) => {
      const curve = areaBounds[rowindex][colIndex][direction];

      // 获取控制元素
      const [control] =
        Array.from(this._objectControlMap.entries()).find((item) => item[1].curve === curve) ?? [];
      if (!control) return;

      // 添加曲线的拖拽图标
      const path = fabricTexture.curvePathMap.get(curve)!;

      // 更新旋转角度
      const points = curve.points.map((point: Coord) =>
        fabric.util.transformPoint(new fabric.Point(point.x, point.y), path.calcOwnMatrix()),
      );
      const deltaY = points[3].y - points[0].y;
      const deltaX = points[3].x - points[0].x;
      const angleRadians = Math.atan2(deltaY, deltaX); // 使用 atan2 处理 deltaX 为 0 的情况
      const angle = angleRadians * (180 / Math.PI);
      control.set({ angle });

      // 更新位置
      const center = path.getCenterPoint();
      control.set({ left: center.x, top: center.y }).setCoords();
    };

    // 添加交互元素
    areaBounds.forEach((row, rowIndex) => {
      row.forEach((col, colIndex) => {
        const bounds = col;
        const directionBeziers = Object.entries(bounds);
        directionBeziers.forEach(([direction]) => {
          const curve = bounds[direction];

          // 拖拽图标
          const control = this._styleSetters.dragIcon(Skew.createDragIcon(this.themeColor));

          // 坐标类型
          const types = (
            {
              top: [VertexType.TOP_LEFT, VertexType.TOP_RIGHT],
              bottom: [VertexType.BOTTOM_LEFT, VertexType.BOTTOM_RIGHT],
              right: [VertexType.TOP_RIGHT, VertexType.BOTTOM_RIGHT],
              left: [VertexType.TOP_LEFT, VertexType.BOTTOM_LEFT],
            } as const
          )[direction] as [VertexType, VertexType];

          this._objectControlMap.set(control, {
            curve,
            rowIndex,
            colIndex,
            vertexTypes: types,
          });

          // 更新位置和角度
          updatePositionAndAngle(rowIndex, colIndex, direction);

          fabricCanvas.add(control);
        });
      });
    });

    // 注册控点变换事件
    const registerObjectTransformEvent = () => {
      const handleMovingControl = (e: fabric.IEvent<Event>) => {
        if (!e.target) return;

        const control = this._objectControlMap.get(e.target);
        if (!control) return;

        const object = e.target;
        const { curve, rowIndex, colIndex, vertexTypes } = control;
        const path = fabricTexture.curvePathMap.get(curve)!;
        const points = curve.points.map((point: Coord) =>
          fabric.util.transformPoint(
            new fabric.Point(point.x - path.pathOffset.x, point.y - path.pathOffset.y),
            path.calcOwnMatrix(),
          ),
        );
        // 旧的中心点
        const oldCenterCoord = {
          x: (points[3].x + points[0].x) / 2,
          y: (points[3].y + points[0].y) / 2,
        };

        // 计算所依附的位置
        let stickyCoord = { x: object.left!, y: object.top! };
        if (points[3].x === points[0].x) {
          stickyCoord.x = points[0].x;
        } else if (points[3].y === points[0].y) {
          stickyCoord.y = points[0].y;
        } else {
          // 计算所在斜面的公式 y=kx+b
          const k = (points[3].y - points[0].y) / (points[3].x - points[0].x);
          const b = points[3].y - k * points[3].x;
          const p0 = {
            x: object.left!,
            y: object.top!,
          };
          const p1 = {
            x: p0.x,
            y: k * p0.x + b,
          };
          const p2 = {
            x: k ? (p0.y - b) / k : p0.x,
            y: p0.y,
          };
          stickyCoord = Math.abs(p0.y - p1.y) < Math.abs(p0.x - p2.x) ? p1 : p2;
        }

        // 所依附的位置也即新的中心点
        const newCenterCoord = stickyCoord;

        // 根据依附的点更新贴图顶点
        fabricTexture.texture!.setVertexCoord(
          rowIndex,
          colIndex,
          vertexTypes[0],
          calcFabricRelativeCoord(
            {
              left: points[0].x + (newCenterCoord.x - oldCenterCoord.x),
              top: points[0].y + (newCenterCoord.y - oldCenterCoord.y),
            },
            path,
          ),
        );
        fabricTexture.texture!.setVertexCoord(
          rowIndex,
          colIndex,
          vertexTypes[1],
          calcFabricRelativeCoord(
            {
              left: points[3].x + (newCenterCoord.x - oldCenterCoord.x),
              top: points[3].y + (newCenterCoord.y - oldCenterCoord.y),
            },
            path,
          ),
        );

        // 更新拖拽按钮的位置和角度
        const directionBeziers = Object.entries(fabricTexture.texture!.areaBoundCurves[0][0]);
        directionBeziers.forEach((i) => {
          updatePositionAndAngle(rowIndex, colIndex, i[0]);
        });

        fabricTexture.requestRender(
          false,
          () => {
            // 如果有控制元素，必须放在最顶端
            this.controlObjects.forEach((control) => {
              fabricCanvas.bringToFront(control);
            });
          },
          { withoutRecord: true },
        );
      };

      // 记录变换数据
      const handleSaveTransformRecord = (e: fabric.IEvent) => {
        const target = e.target;
        if (!target || e.action !== 'drag') return;
        if (this.controlObjects.includes(target)) {
          fabricTexture.record();
        }
      };
      fabricCanvas.on('object:moving', handleMovingControl);
      fabricCanvas.on('object:modified', handleSaveTransformRecord);
      return () => {
        fabricCanvas.off('object:moving', handleMovingControl);
        fabricCanvas.off('object:modified', handleSaveTransformRecord);
      };
    };

    // 注册拖动修改透视位置事件
    const registerDragResizeEvent = () => {
      // 添加拖动修改透视位置事件
      const handleMoveDownBefore = (e: fabric.IEvent<Event>) => {
        // 仅左键有效
        if ((e as any).e.buttons !== 1) return;
        if (e.target && e.target.selectable) return;

        let hasResized = false;

        fabricCanvas.selection = false;
        const leftTop = calcFabricCanvasCoord(fabricCanvas, e.pointer!);

        const handleMove = (e: fabric.IEvent<Event>) => {
          const bottomRight = calcFabricCanvasCoord(fabricCanvas, e.pointer!);

          // 尺寸太小不进行操作
          if (
            !hasResized &&
            (Math.abs(bottomRight.x - leftTop.x) < 50 || Math.abs(bottomRight.y - leftTop.y) < 50)
          )
            return;

          const leftTopPoint = calcFabricRelativeCoord(
            { left: leftTop.x, top: leftTop.y },
            fabricTexture.curvePathMap.get(fabricTexture.texture!.areaBoundCurves[0][0].top)!,
          );
          const bottomRightPoint = calcFabricRelativeCoord(
            { left: bottomRight.x, top: bottomRight.y },
            fabricTexture.curvePathMap.get(fabricTexture.texture!.areaBoundCurves[0][0].bottom)!,
          );

          fabricTexture.texture!.setVertexCoord(0, 0, VertexType.TOP_LEFT, leftTopPoint);
          fabricTexture.texture!.setVertexCoord(0, 0, VertexType.TOP_RIGHT, {
            x: bottomRightPoint.x,
            y: leftTopPoint.y,
          });
          fabricTexture.texture!.setVertexCoord(0, 0, VertexType.BOTTOM_LEFT, {
            x: leftTopPoint.x,
            y: bottomRightPoint.y,
          });
          fabricTexture.texture!.setVertexCoord(0, 0, VertexType.BOTTOM_RIGHT, bottomRightPoint);
          fabricTexture.render(true, { withoutRecord: true });

          hasResized = true;
        };

        const handleUp = (e: fabric.IEvent<Event>) => {
          if (hasResized) fabricTexture.record();
          fabricCanvas.selection = true;
          fabricCanvas.off('mouse:move', handleMove);
          fabricCanvas.off('mouse:up', handleUp);
        };

        fabricCanvas.on('mouse:move', handleMove);
        fabricCanvas.on('mouse:up', handleUp);
      };
      fabricCanvas.on('mouse:down:before', handleMoveDownBefore);

      return () => {
        fabricCanvas.off('mouse:down:before', handleMoveDownBefore);
      };
    };

    // 注册各类事件并持有它们的取消注册回调
    const { enableDragResize } = this.options;

    const registers = [
      [enableDragResize, registerDragResizeEvent],
      [true, registerObjectTransformEvent],
    ] as const;

    const unregisters = registers.map(([enable, register]) => enable && register());

    return () => {
      // 取消各类事件注册
      unregisters.forEach((unregister) => {
        if (typeof unregister === 'function') unregister();
      });

      // 移除所有交互元素
      fabricCanvas.remove(...this._objectControlMap.keys());
      this._objectControlMap.clear();
    };
  }
}

export default Skew;
